import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import { subscribeMatchingWeekSettings } from '../../../api/settings.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { Language } from '@adornis/translation-core/languages.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import * as m from '../../../../localization/out/messages.js';
import datenschutzImg from '../../assets/Datenschutz_Bild.png';

// Public Privacy page with language switch (de/en)
export const PublicPrivacy = stateful(() => {
  useObservable(() => globalLanguageSubject, []);

  const settingsQuery = useQuery(
    () => subscribeMatchingWeekSettings()({ privacyPolicy: selectionSet(() => TextMultipleLangItem, 1) }),
    [],
  );

  const translations = settingsQuery.value
    ? TextMultipleLangItem.mapAllTranslationsByLang(settingsQuery.value.privacyPolicy ?? [])
    : {};

  const text = (translations as Record<Language, string | undefined>)[globalLanguageSubject.value] ?? '';

  return html`
    <div class="p-4 sm:p-8 flex flex-col items-center h-screen overflow-y-auto">
      <div class="max-w-4xl w-full">
        <div class="text-center mb-6">
          <h1 class="text-3xl font-bold">${m.privacy_policy()}</h1>
        </div>

        <div class="flex justify-center mb-4">
          <div class="join">
            <button
              class=${`btn join-item ${globalLanguageSubject.value === Language.GERMAN ? 'btn-primary' : 'btn-ghost'}`}
              @click=${() => globalLanguageSubject.next(Language.GERMAN)}
            >
              DE
            </button>
            <button
              class=${`btn join-item ${globalLanguageSubject.value === Language.ENGLISH ? 'btn-primary' : 'btn-ghost'}`}
              @click=${() => globalLanguageSubject.next(Language.ENGLISH)}
            >
              EN
            </button>
          </div>
        </div>

        ${
          settingsQuery.loading
            ? html`
                <div class="card bg-base-100 shadow">
                  <div class="card-body items-center justify-center">
                    <span class="loading loading-spinner"></span>
                    <span>Loading…</span>
                  </div>
                </div>
              `
            : ''
        }
        ${
          settingsQuery.error
            ? html`
                <div class="alert alert-error mb-4">
                  <span class="icon-[material-symbols--error]"></span>
                  <span>${settingsQuery.error.message ?? 'Failed to load privacy policy'}</span>
                </div>
              `
            : ''
        }
        ${
          settingsQuery.value
            ? html`
                <div class="card bg-base-100 shadow">
                  <div class="card-body">
                    ${
                      text
                        ? html`<div class="whitespace-pre-wrap leading-relaxed">${unsafeHTML(text)}</div>`
                        : html`<div class="opacity-70">
                            No ${globalLanguageSubject.value.toUpperCase()} privacy policy available.
                          </div>`
                    }
                  </div>
                  <img src=${datenschutzImg} alt="Datenschutz Informationen" />
                </div>
              `
            : ''
        }
      </div>
    </div>
  `;
});
